import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Toaster } from "sonner";
import axios from "axios";
import { Clock, Phone, MapPin, CreditCard, ShoppingBag } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";

const _BU = API_URL || "";
const _isProxy = (() => { try { return window.location.hostname !== new URL(_BU).hostname && !window.location.hostname.includes('localhost'); } catch { return false; } })();
const API = _isProxy ? "/api" : `${_BU}/api`;
const DEFAULT_LOGO = "";

export function OrderConfirmation({ orderConfirmed, restaurant, customerName, confirmedCart, tableName, tableNumber, fullAddress, paymentMethod, changeFor, restaurantId, onNewOrder, themeColor }) {
  const pc = themeColor || "#059669";
  const [whatsappSent, setWhatsappSent] = useState(false);
  const waitMin = orderConfirmed?.wait_time_min || restaurant?.wait_time_min || 30;
  const waitMax = orderConfirmed?.wait_time_max || restaurant?.wait_time_max || 50;
  // Prefer dedicated whatsapp_number field; fall back to phone. Strip to digits only.
  const _rawWaPhone = (restaurant?.whatsapp_number || restaurant?.phone || "").replace(/\D/g, "");
  // Ensure international format — if number has < 10 digits it's missing the country code, skip it
  const whatsappPhone = _rawWaPhone.length >= 10 ? _rawWaPhone : "";
  const paymentLabels = { online: "Pagamento Online", cash: "Dinheiro", card_restaurant: "Cartao no Restaurante", bizum: "Bizum", mbway: "MBWay" };
  const orderId = orderConfirmed?.order_id || orderConfirmed?.id;

  // Auto-send WhatsApp on mount
  useEffect(() => {
    if (whatsappSent) return;
    const phone = orderConfirmed?.customer_phone;
    const orderId = orderConfirmed?.order_id || orderConfirmed?.id;
    if (phone && orderId) {
      setWhatsappSent(true);
      axios.post(`${API}/public/order/${orderId}/send-whatsapp`, { customer_phone: phone })
        .then(res => {
          if (res.data?.success) toast.success("Confirmacao enviada no seu WhatsApp!");
        })
        .catch(() => {});
    }
  }, [orderConfirmed, whatsappSent]);

  return (
    <div className="min-h-screen bg-[#faf9f6] flex flex-col items-center py-8 px-4">
      <div className="max-w-md w-full space-y-5" data-testid="order-confirmed">
        {/* Success Header */}
        <div className="text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${pc}18` }}>
            <svg className="w-10 h-10" style={{ color: pc }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Pedido Recebido!</h1>
          {orderConfirmed.payment_status === "paid" && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold mt-2" style={{ backgroundColor: `${pc}18`, color: pc }}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              Pagamento confirmado
            </div>
          )}
        </div>

        {/* Points earned gamification */}
        {(orderConfirmed?.points_earned > 0 || orderConfirmed?.prime_delivery_applied) && (
          <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
            <span className="text-2xl shrink-0">{orderConfirmed?.prime_delivery_applied ? "🚀" : "⭐"}</span>
            <div>
              {orderConfirmed?.points_earned > 0 && (
                <p className="font-bold text-purple-800 text-sm">+{orderConfirmed.points_earned} pontos ganhos!</p>
              )}
              {orderConfirmed?.prime_delivery_applied && (
                <p className="font-bold text-emerald-700 text-sm">
                  Entrega grátis PRIME aplicada{orderConfirmed.prime_delivery_saved ? ` · poupaste €${Number(orderConfirmed.prime_delivery_saved).toFixed(2)}` : ""}
                </p>
              )}
              <p className="text-xs text-purple-500 mt-0.5">Acumulado no teu cartão de fidelidade</p>
            </div>
          </div>
        )}

        {/* Estimated Time */}
        <div className="rounded-xl p-4 text-center border" style={{ backgroundColor: `${pc}0a`, borderColor: `${pc}30` }} data-testid="wait-time-display">
          <Clock className="w-6 h-6 mx-auto mb-1" style={{ color: pc }} />
          <p className="text-sm font-medium" style={{ color: pc }}>Tempo estimado</p>
          <p className="text-2xl font-bold" style={{ color: pc }}>{waitMin}-{waitMax} min</p>
          <p className="text-xs mt-1" style={{ color: `${pc}99` }}>Enviaremos atualizacoes pelo WhatsApp</p>
        </div>

        {/* Order Summary Card */}
        <div className="bg-white rounded-xl p-5 shadow-sm border space-y-3">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Pedido #{(orderId || "").slice(0, 8).toUpperCase()}</span>
            <span className="font-bold text-lg" style={{ color: pc }}>{orderConfirmed.total ? `${orderConfirmed.total.toFixed(2)}EUR` : ""}</span>
          </div>
          {confirmedCart.length > 0 && (
            <div className="border-t pt-3 space-y-1.5">
              {confirmedCart.map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-gray-600">{item.quantity}x {item.name}</span>
                  <span className="font-medium">{(item.price * item.quantity).toFixed(2)}EUR</span>
                </div>
              ))}
            </div>
          )}
          <div className="border-t pt-3 space-y-1.5 text-xs text-gray-500">
            {(tableName || tableNumber) && (
              <div className="flex items-center gap-1.5"><ShoppingBag className="w-3.5 h-3.5" />{tableName || `Mesa ${tableNumber}`}</div>
            )}
            {fullAddress && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{fullAddress}</div>}
            <div className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" />{paymentLabels[paymentMethod] || paymentMethod}</div>
            {changeFor && <span>Troco para {changeFor}EUR</span>}
          </div>
        </div>

        {/* Track Order Button */}
        {orderId && (
          <a href={`/track/${orderId}`}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-md transition"
            data-testid="track-order-btn">
            <MapPin className="w-5 h-5" />
            Acompanhar meu pedido em tempo real
          </a>
        )}

        {/* WhatsApp Contact — only shown when a valid phone is available */}
        {whatsappPhone && (
          <a
            href={`https://wa.me/${whatsappPhone}?text=${encodeURIComponent(`Olá! Quero acompanhar o meu pedido #${(orderId || "").slice(0, 8).toUpperCase()}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full h-12 bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-md transition"
            data-testid="contact-whatsapp-btn"
          >
            <Phone className="w-5 h-5" />
            Entrar em contato pelo WhatsApp
          </a>
        )}

        <Button onClick={onNewOrder} className="w-full h-12 text-white font-semibold" style={{ backgroundColor: pc }} data-testid="new-order-after-confirm">
          Fazer outro pedido
        </Button>

        {restaurantId && (
          <a href={`/portal/${restaurantId}`} className="w-full h-12 border-2 font-semibold rounded-xl flex items-center justify-center gap-2 transition hover:opacity-80" style={{ color: pc, borderColor: `${pc}60` }} data-testid="customer-portal-link">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
            Ver meus pontos de fidelidade
          </a>
        )}

        <p className="text-center text-xs text-gray-400">Recebera atualizacoes automaticas pelo WhatsApp.</p>
      </div>
      <Toaster position="top-center" />
    </div>
  );
}
